import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  // base styles
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium " +
    "transition-all disabled:pointer-events-none disabled:opacity-50 " +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 " +
    "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500",
  {
    variants: {
      variant: {
        // main blue button (used for general primary actions)
        default: "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm",

        // 🔥 selected variant -> black background
        selected:
          "bg-[#DDEEFF] text-[#3097FF] border border-[#3097FF] " + "hover:bg-[#CCDFFF] active:bg-[#D0D0FF] shadow-sm",
        // red / danger
        destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm",

        // outlined neutral button
        outline:
          "bg-[#FFFFFF] text-[#3097FF] border border-[#D5C8FF] " + "hover:bg-[#CCDFFF] active:bg-[#D0D0FF] shadow-sm",
        // subtle gray secondary
        secondary: "bg-gray-100 text-gray-800 hover:bg-gray-200 active:bg-gray-300",

        // ghost/text-only button
        ghost: "text-gray-700 hover:bg-gray-100 active:bg-gray-200",

        // link style
        link: "text-blue-600 hover:underline underline-offset-4",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4 text-base",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
